import React, { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car } from "@phosphor-icons/react";

export default function Vehicles() {
  const { t, lang } = useLang();
  const [vehicles, setVehicles] = useState([]);
  const [condition, setCondition] = useState("all");
  const [brand, setBrand] = useState("all");

  const load = async () => {
    const params = new URLSearchParams();
    if (condition !== "all") params.set("condition", condition);
    if (brand !== "all") params.set("brand", brand);
    const { data } = await api.get(`/vehicles?${params.toString()}`);
    setVehicles(data);
  };

  useEffect(() => { load(); }, [condition, brand]);

  const brands = ["all", ...new Set(vehicles.map((v) => v.brand))];

  return (
    <>
      <Topbar title={t("veh.title")} subtitle={t("veh.subtitle")} />
      <div className="p-8 space-y-4 animate-fade-in flex-1 overflow-y-auto min-h-0">
        <div className="flex items-center gap-3">
          <Tabs value={condition} onValueChange={setCondition}>
            <TabsList>
              <TabsTrigger value="all" data-testid="veh-filter-all">{t("common.all")}</TabsTrigger>
              <TabsTrigger value="new" data-testid="veh-filter-new">{t("veh.new")}</TabsTrigger>
              <TabsTrigger value="used" data-testid="veh-filter-used">{t("veh.used")}</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="w-44" data-testid="veh-brand-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              {brands.map((b) => <SelectItem key={b} value={b}>{b === "all" ? t("common.all") : b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {vehicles.length === 0 && (
            <div className="col-span-full text-center py-16 text-muted-foreground border border-dashed border-border rounded-md">
              <Car size={40} className="mx-auto mb-2 opacity-40"/>
              {t("common.empty")}
            </div>
          )}
          {vehicles.map((v) => (
            <div key={v.id} className="border border-border rounded-md overflow-hidden bg-card hover:-translate-y-0.5 transition-all" data-testid={`vehicle-${v.id}`}>
              <div className="aspect-video bg-secondary overflow-hidden">
                {v.image_url ? (
                  <img src={v.image_url} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Car size={32} className="text-muted-foreground"/></div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <div className="overline">{v.brand}</div>
                    <div className="font-heading font-semibold">
                      {v.model}
                      {v.version ? <span className="text-muted-foreground font-normal"> · {v.version}</span> : null}
                    </div>
                  </div>
                  <Badge className={v.condition === "new" ? "bg-emerald-500 text-white" : "bg-slate-500 text-white"}>
                    {v.condition === "new" ? t("veh.new") : t("veh.used")}
                  </Badge>
                </div>
                <div className="font-heading text-xl font-bold text-primary mt-2">
                  {v.price_usd
                    ? `US$${v.price_usd.toLocaleString()}`
                    : `RD$${v.price.toLocaleString()}`}
                </div>
                {v.monthly_installment_rd ? (
                  <div className="text-xs text-muted-foreground">
                    Cuota ref. RD${v.monthly_installment_rd.toLocaleString()}/mes
                  </div>
                ) : null}
                <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                  <div>{v.year} · {v.transmission} · {v.fuel}</div>
                  {v.condition === "used" && v.mileage > 0 ? (
                    <div>{v.mileage.toLocaleString()} km</div>
                  ) : null}
                  <div>{v.branch}</div>
                  {v.condition === "new" && v.brand ? (
                    <a
                      href={`https://www.santodomingomotors.com.do/${v.brand.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-")}/`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline text-xs"
                      data-testid={`vehicle-source-${v.id}`}
                    >
                      Ver {v.brand} en santodomingomotors.com.do →
                    </a>
                  ) : v.source_url ? (
                    <a
                      href={v.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline text-xs"
                      data-testid={`vehicle-source-${v.id}`}
                    >
                      Ver en seminuevossdm.com.do →
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
